import { createContext, useContext, useState } from 'react';
import type { ReactNode } from 'react';
import { api } from '../services/api';
import type { ApiClient } from '../services/api';

export interface Droplet {
  id: string;
  name: string;
  ip: string;
  // Secondary droplet's feed id on the home API (GET /api/v1/status/<feedId>);
  // undefined = the home droplet's own feed (GET /api/v1/status).
  feedId?: string;
  description: string;
}

// Every droplet's data comes from the home droplet's API. Secondary droplets
// run no console service of their own: NYC3 pulls each one's read-only SSH
// probe and publishes a sanitized feed (see deploy.3pplea.com
// scripts/console-remote-collector.py), so no droplet exposes an HTTP
// endpoint and the browser never needs a per-droplet token.
export const DROPLETS: Droplet[] = [
  {
    id: 'droplet1',
    name: 'Droplet 1',
    ip: '159.89.183.132',
    description: 'NYC3 — 3pplea apps',
  },
  {
    id: 'droplet2',
    name: 'Droplet 2',
    ip: '209.97.162.57',
    feedId: 'sgp1',
    description: 'SGP1 — Roveera staging',
  },
  {
    id: 'droplet3',
    name: 'Droplet 3',
    ip: '167.172.207.190',
    feedId: 'sfo2',
    description: 'SFO2 — Trivex log receiver',
  },
];

interface DropletContextValue {
  selectedDroplet: Droplet;
  setSelectedDroplet: (droplet: Droplet) => void;
  // The home droplet's client — the only API, with real username/password +
  // session-cookie auth. Used for login/logout/session/forgot-/reset-password
  // and for every droplet's data (pick the droplet via selectedDroplet.feedId).
  api: ApiClient;
  authApi: ApiClient;
}

const DropletContext = createContext<DropletContextValue | null>(null);

export function DropletProvider({ children }: { children: ReactNode }) {
  const [selectedDroplet, setSelectedDroplet] = useState<Droplet>(DROPLETS[0]);

  return (
    <DropletContext.Provider value={{ selectedDroplet, setSelectedDroplet, api, authApi: api }}>
      {children}
    </DropletContext.Provider>
  );
}

export function useDroplet(): DropletContextValue {
  const ctx = useContext(DropletContext);
  if (!ctx) throw new Error('useDroplet must be used within DropletProvider');
  return ctx;
}
